import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { object, string, type ObjectSchema } from "yup";
import { Card, Flex } from "@jsq/ds/core";
import { Form, FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import {
  NAME_NOT_UNIQUE_ERROR,
  useDataPackageNameValidator,
  type DataPackageNameValidator,
} from "../useDataPackageNameValidator";
import * as styles from "./styles_vanilla.css";

const MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 200;

export type TemplateSettingsFormValues = {
  name: string;
  description: string;
};

const createValidationSchema = (
  validateName: DataPackageNameValidator,
): ObjectSchema<TemplateSettingsFormValues> =>
  object({
    name: string()
      .required("Name this data package")
      .trim()
      .min(MIN_LENGTH, `Must be at least ${MIN_LENGTH} characters`)
      .max(NAME_MAX_LENGTH, `Maximum ${NAME_MAX_LENGTH} characters`)
      .test("unique-name", NAME_NOT_UNIQUE_ERROR, async function testUnique(value) {
        if (!value) return true;
        const error = await validateName(value);
        if (!error) return true;
        return this.createError({ message: error });
      }),
    description: string()
      .trim()
      .test(
        "min-if-provided",
        `Must be at least ${MIN_LENGTH} characters`,
        (value) => !value || value.length >= MIN_LENGTH,
      )
      .max(DESCRIPTION_MAX_LENGTH, `Maximum ${DESCRIPTION_MAX_LENGTH} characters`)
      .default("")
      .defined(),
  });

const NextButton = () => {
  const { values, errors } = useFormikContext<TemplateSettingsFormValues>();
  const isNameEmpty = values.name.trim().length < MIN_LENGTH;
  const hasErrors = Object.keys(errors).length > 0;
  return <SubmitButton disabled={isNameEmpty || hasErrors}>Next</SubmitButton>;
};

export type TemplateSettingsStepProps = {
  initialValues?: TemplateSettingsFormValues;
  onNext: (values: TemplateSettingsFormValues) => void;
  arenaId: number | null;
};

export const TemplateSettingsStep = ({
  initialValues = { name: "", description: "" },
  onNext,
  arenaId,
}: TemplateSettingsStepProps) => {
  const handleSubmit = useCallback(
    (values: TemplateSettingsFormValues) => {
      onNext(values);
    },
    [onNext],
  );

  const validateName = useDataPackageNameValidator({ arenaId });
  const validationSchema = useMemo(() => createValidationSchema(validateName), [validateName]);

  return (
    <Card className={styles.formCard} title="Data package settings">
      <Form<TemplateSettingsFormValues>
        initialValues={initialValues}
        yupValidationSchema={validationSchema}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <FormTextInput
            name="name"
            label="Data package name"
            data-testid="data-package-name-input"
            isFullWidth
          />
          <FormTextInput
            name="description"
            label="Description"
            data-testid="data-package-description-input"
            isFullWidth
          />
          <Flex justifyContent="flex-end">
            <NextButton />
          </Flex>
        </Flex>
      </Form>
    </Card>
  );
};
